import axios from "axios";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { setGeneratedOpenAIRequests } from "../../../actions/projectActions";
import {
  getGeneratedBenefits,
  getGeneratedChallenges,
  getGeneratedImage,
  getGeneratedNamesForIdea,
} from "../../../api/ai";
import {
  createIdea,
  createIssuesWithMessages,
  deleteIdea,
  getMessagesByIssuesIds,
  updateIdea,
} from "../../../api/eureka";
import { archiveIdeas } from "../../../api/trash.js";
import { uploadImage } from "../../../api/uploads";
import ErrorMessageModal from "../../../components/dialogs/ErrorMessageModal";
import { areObjectsEqual } from "../../../helpers/areObjectsEqual";
import { getTranslation } from "../../../helpers/getLanguage";
import { getUpdatedIdea } from "../../../helpers/getUpdatedIdea";
import { useConfirmationDeleteModal } from "../../../hooks/ideas/useConfirmationDeleteModal";
import { useUndoResetIdea } from "../../../hooks/useUndoResetIdea.js";
import WarningNotTokensModal from "../../ComponentsDashboard/WarningNotTokensModal";
import BuyMoreAICreditsModal from "../BuyMoreAICreditsModal";
import CheckoutModal from "../CheckoutModal";
import { IdeaModalFooter } from "../commonComponents/IdeaModalFooter";
import { IdeaNavigationFooter } from "../commonComponents/IdeaNavigationFooter";
import ConfirmationModal from "../ConfirmationModal";
import Dialog from "../Dialog";
import TrashModal from "../TrashModal/index.jsx";
import EurekaModalHeader from "./EurekaModalHeader";
import EurekaModalTabs from "./EurekaModalTabs";
import "./styles.css";

const IDEAS_IDENTIFICATION = {
  EMPTY_IDEA: 0,
  IN_PROGRESS_IDEA: 1,
  NOT_FEASIBLE_IDEA: 2,
  COMPLETED_IDEA: 3,
};

const initialState = {
  isIdeaFeasible: null,
  score: null,
  feasibleScore: null,
  potentialBenefitsOfEureka: [{ name: "", description: "", target: [], userInfo: "" }],
  issues: [{ comment: "", description: "", createdByUser: "" }],
  issuesMessages: [],
  description: "",
  name: "",
  image: { name: "", originalName: "", id: "" },
  imagesLibrary: [],
};

const initialPaymentInfo = {
  intervalCount: 1,
  quantity: 1,
  key: "",
  interval: null,
  openAIRequests: null,
  total: null,
  promoCode: "",
  promoCodeOpenAI: "",
};

const initialUpload = {
  progress: 100,
  cancel: null,
  name: "",
  date: "",
  size: 0,
  uploaded: false,
  uploadName: "",
};

const isDefaultCategory = (value) => ["Consumer", "Company", "Other"].includes(value);

const EurekaEditModal = ({
  closeDialog,
  edit,
  projectId,
  projectName,
  eurekaIdea,
  teamId,
  categories = [],
  productType,
  productName,
  language,
  history,
  setGeneratedOpenAIRequests,
  ...props
}) => {
  const [eureka, setEureka] = useState(_.cloneDeep(initialState));
  const [currentTab, setCurrentTab] = useState(0);
  const [isNew, setIsNew] = useState(!edit);
  const [eurekaId, setEurekaId] = useState(eurekaIdea.id);
  const [savedEureka, setSavedEureka] = useState(_.cloneDeep(initialState));
  const [isPending, setIsPending] = useState({ create: false, update: false });
  const [uploadInfo, setUploadInfo] = useState({ ...initialUpload });
  const [isClosing, setIsClosing] = useState(false);
  const [customCategories, setCustomCategories] = useState([...categories]);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isCompletedIdea, setIsCompletedIdea] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [error, setError] = useState({
    openErrorModal: false,
    message: "",
    headerError: "",
  });
  const [suggestedOptions, setSuggestedOptions] = useState([]);
  const [selectedGenerateOptions, setSelectedGenerateOptions] = useState(new Set());
  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const [isOpenGenerateImageModal, setIsOpenGenerateImageModal] = useState(false);
  const [generatedImage, setGeneratedImage] = useState("");
  const [potentialGeneratedImageFile, setPotentialGeneratedImageFile] = useState(null);
  const [potentialGeneratedImageUrl, setPotentialGeneratedImageUrl] = useState("");
  const [newGeneratedImage, setNewGeneratedImage] = useState({
    isNew: false,
    urlImage: "",
  });
  const [isOpenWarningModal, setIsOpenWarningModal] = useState(false);
  const [isOpenBuyCreditsgModal, setIsOpenBuyCreditsgModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(initialPaymentInfo);
  const [isEnoghTokensForImage, setIsEnoghTokensForImage] = useState(true);
  const {
    isConfirmationModalOpen,
    handleCloseConfirmationModalOpen,
    handleOpenConfirmationModalOpen,
    isConfirmationDeleteModalOpen,
    handleCloseConfirmationDeleteModalOpen,
    handleOpenConfirmationDeleteModalOpen,
  } = useConfirmationDeleteModal();
  const isDisabledSave = areObjectsEqual(eureka, initialState);
  const disabledNextButton = currentTab === 1 && (eureka.score === null || eureka.score === undefined);

  useEffect(() => {
    (async () => {
      let componentExists = true;
      const issuesParams = eurekaIdea.issues ? eurekaIdea.issues.map((issue) => issue.id) : [];
      const params = { issues: issuesParams };

      const onSuccess = (response) => {
        if (componentExists) setMessages((prev) => [...prev, ...response]);
      };

      const onError = (error) => {
        console.error(error);
        handleClose(true, { ...isPending, update: false });
      };

      await getMessagesByIssuesIds(params, onSuccess, onError);

      return () => (componentExists = false);
    })();
  }, []);

  useEffect(() => {
    if (edit) updateValues();
  }, [eurekaIdea, edit, messages]);

  useEffect(() => {
    if (!isClosing) {
      const isUpToDate = savedEureka.image.name === eureka.image.name;
      if (!isUpToDate) {
        handleSave();
      }
    }
  }, [eureka.image, isClosing, savedEureka]);

  useEffect(() => {
    if (eureka && eureka.score && eureka.feasibleScore) {
      setIsCompletedIdea(true);
    } else {
      setIsCompletedIdea(false);
    }
  }, [eureka.score, eureka.feasibleScore]);

  const getStatus = () => {
    const currentEureka = getEureka();
    const { isIdeaFeasible, score, description, feasibleScore, issues, potentialBenefitsOfEureka, name, image } =
      currentEureka;

    if (isIdeaFeasible === false || score === 0 || feasibleScore === 0) {
      return IDEAS_IDENTIFICATION.NOT_FEASIBLE_IDEA;
    }

    if (isIdeaFeasible && score > 0) {
      return IDEAS_IDENTIFICATION.COMPLETED_IDEA;
    }

    if (
      !isIdeaFeasible &&
      score === null &&
      feasibleScore === null &&
      (issues.length === 1 || issues.length === 0) &&
      !issues[0]?.comment.trim() &&
      potentialBenefitsOfEureka.length === 1 &&
      !potentialBenefitsOfEureka[0].name.trim() &&
      !description.trim() &&
      !name.trim() &&
      !image.id
    ) {
      return IDEAS_IDENTIFICATION.EMPTY_IDEA;
    }

    return IDEAS_IDENTIFICATION.IN_PROGRESS_IDEA;
  };

  const getEurekaDetails = () => {
    const currentEureka = getEureka();

    return {
      name: currentEureka.name,
      potentialBenefitsOfEureka: currentEureka.potentialBenefitsOfEureka.filter((el) => el.name),
      isIdeaFeasible: currentEureka.isIdeaFeasible,
      issues: currentEureka.issues.filter((issue) => issue),
      description: currentEureka.description,
      rating: currentEureka.score,
      feasibleRating: currentEureka.feasibleScore,
      image: currentEureka.image,
      imagesLibrary: currentEureka.imagesLibrary,
      status: getStatus(),
    };
  };

  const getIssuesWithMessages = () => {
    const currentEureka = getEureka();
    const deletedIssues = currentEureka.deletedIssues ?? [];

    const getMessagesByIssueIdWithDeletedMessages = (issueId, issueIndex) => {
      const filteredMessages = currentEureka.issuesMessages.filter((message) =>
        message.issueId ? message.issueId === issueId : message.issueIndex === issueIndex,
      );
      const deletedMessages = currentEureka.deletedIssuesMessages ?? [];

      return [...filteredMessages, ...deletedMessages];
    };

    return [
      ...currentEureka.issues.map((issue, index) => ({
        ...issue,
        messages: getMessagesByIssueIdWithDeletedMessages(issue.id, index),
      })),
      ...deletedIssues,
    ];
  };

  const handleClose = (shouldClose, pendingState) => {
    setIsPending(pendingState);
    if (shouldClose) {
      resetEureka();
      handleCloseDialog();
    }
  };

  const createEureka = (shouldClose = false) => {
    if (compareIdeas(initialState, getEureka())) {
      handleClose(shouldClose, { ...isPending, create: false });
      return;
    }

    if (isPending.create) return;

    shouldClose && setIsClosing(true);

    const params = {
      customCategories,
      id: eurekaId,
      productId: projectId,
      teamId: teamId || "",
      ...getEurekaDetails(false),
    };

    params.imagesLibrary = params.imagesLibrary.map((image) => {
      const imageCopy = { ...image };
      delete imageCopy.imageSource;
      return imageCopy;
    });

    delete params.image.imageSource;

    setIsPending({ ...isPending, create: true });

    const onSuccess = (response) => {
      const issues = getIssuesWithMessages() || [];

      const issuesParams = {
        ideaId: response.id || "",
        issues,
      };

      const onSuccessUpdateIssues = () => {};

      const onErrorUpdateIssues = (error) => {
        console.error(error);
      };

      if (shouldClose) {
        createIssuesWithMessages(issuesParams, onSuccessUpdateIssues, onErrorUpdateIssues);
      }

      setIsNew(false);
      setEurekaId(response.id);
      handleClose(shouldClose, { ...isPending, create: false });
    };

    const onError = (error) => {
      console.error(error);
      handleClose(shouldClose, { ...isPending, create: false });
    };

    createIdea(params, onSuccess, onError);
  };

  const getEureka = () => {
    return { ...eureka, image: { ...eureka.image } };
  };

  const { checkedTab, updateHistory, undoResetIdea } = useUndoResetIdea(getEureka, setEureka, currentTab, "eureka");

  const resetEureka = () => {
    const currentEureka = getEureka();
    updateHistory(currentEureka);

    const issuesToDelete = currentEureka.issues.map((issue) => ({ id: issue.id })) || [];
    const params = {
      issues: issuesToDelete,
      ideaId: eurekaId || "",
    };
    const onSuccess = () => {};
    const onError = (error) => {
      console.error(error);
    };

    params.ideaId && createIssuesWithMessages(params, onSuccess, onError);

    currentEureka.potentialBenefitsOfEureka.forEach((benefit) => {
      benefit.target.forEach((target) => {
        if (isDefaultCategory(target)) return;

        removeCustomCategory(target);
      });
    });

    const updatedIdea = getUpdatedIdea(eureka, currentTab, "eureka", initialState);

    setEureka({ ...initialState, ...updatedIdea });

    if (currentTab === 0) {
      updateUploadInfo({ ...initialUpload });
    }

    setIsNew(!edit);
  };

  const deleteEureka = () => {
    const currentEureka = getEureka();

    if (!eurekaId) {
      handleCloseDialog();
    }

    const issuesToDelete = currentEureka.issues.map((issue) => ({ id: issue.id })) || [];
    const params = {
      issues: issuesToDelete,
      ideaId: eurekaId || "",
    };

    const onSuccess = () => {
      handleCloseConfirmationDeleteModalOpen();
      handleCloseDialog();
    };

    const onError = (error) => {
      console.error(error);
    };

    params.ideaId && deleteIdea(params, onSuccess, onError);
  };

  const archivedEureka = () => {
    if (!eurekaId) {
      handleCloseDialog();
    }

    const params = {
      ideas: { eurekasIds: [eurekaId] },
      productId: projectId,
    };

    const onSuccess = ({ trashFull }) => {
      if (trashFull) {
        handleOpenConfirmationDeleteModalOpen();
      } else {
        handleCloseDialog();
      }
    };

    const onError = (error) => {
      console.error(error);
    };

    params.ideas && archiveIdeas(params, onSuccess, onError);
  };

  const updateValues = () => {
    updateSubtractionDetails(false);
  };

  const updateSubtractionDetails = () => {
    const currentEureka = eurekaIdea;

    if (Object.keys(currentEureka).length) {
      const benefitsOfEureka = currentEureka.potentialBenefitsOfEureka;
      const hasBenefitsOfEureka = benefitsOfEureka && benefitsOfEureka.length > 0;
      const issues = currentEureka.issues;
      const hasIssues = issues && issues.length > 0;
      const issuesMessages = [...messages, ...(currentEureka.issuesMessages ? currentEureka.issuesMessages : [])];
      const hasIssuesMessages = issuesMessages && issuesMessages.length > 0;
      const image = currentEureka.image;
      const currentRating =
        currentEureka.rating === null ? null : currentEureka.rating === 0 ? 0 : currentEureka.rating;
      const currentFeasibleRating =
        currentEureka.feasibleRating === null
          ? null
          : currentEureka.feasibleRating === 0
            ? 0
            : currentEureka.feasibleRating;

      const params = {
        isIdeaFeasible: currentEureka.isIdeaFeasible,
        score: currentRating,
        feasibleScore: currentFeasibleRating,
        potentialBenefitsOfEureka: (hasBenefitsOfEureka && benefitsOfEureka) || [
          { name: "", description: "", target: [], userInfo: "" },
        ],
        issues: (hasIssues && issues) || [{ comment: "", description: "", createdByUser: "" }],
        issuesMessages: (hasIssuesMessages && issuesMessages) || [],
        name: currentEureka.name || "",
        description: currentEureka.description || "",
        image: image || initialState.image,
        imagesLibrary: currentEureka.imagesLibrary || initialState.imagesLibrary,
      };

      if (image && image.name) {
        updateUploadInfo({
          ...initialUpload,
          name: image.originalName,
          date: image.date,
          size: image.size,
          uploadName: image.name,
          uploaded: true,
          id: image.id,
        });
      }
      const updatedDependency = { ...getEureka(), ...params };
      handleSetEureka(updatedDependency);
      setSavedEureka({ ...updatedDependency });
    } else {
      handleSetEureka({ ...initialState });
      setSavedEureka({ ...initialState });
    }
  };

  const selectCurrentEurekaImageFromLibrary = (index) => {
    const currentEureka = getEureka();
    handleSetEureka({ ...currentEureka, image: currentEureka.imagesLibrary[index] });
  };
  const removeImageFromLibrary = (id) => {
    const currentEureka = getEureka();
    const imagesLibrary = [...currentEureka.imagesLibrary];
    const indexOfSelectedImage = imagesLibrary.findIndex((image) => image.id === id);

    if (id === currentEureka.image.id) {
      const newCurrentImageIndex = indexOfSelectedImage
        ? imagesLibrary.length % indexOfSelectedImage
        : imagesLibrary.length - 1;

      handleSetEureka({
        ...currentEureka,
        image: imagesLibrary.length - 1 ? imagesLibrary[newCurrentImageIndex] : initialState.image,
        imagesLibrary: imagesLibrary.filter((image) => image.id !== id),
      });
      return newCurrentImageIndex;
    }
    handleSetEureka({ ...currentEureka, imagesLibrary: imagesLibrary.filter((image) => image.id !== id) });
    return imagesLibrary.findIndex((image) => image.id === currentEureka.image.id);
  };

  const updateEureka = (shouldClose = false) => {
    if (!eurekaId) return;

    shouldClose && setIsClosing(true);

    const params = {
      id: eurekaId,
      productId: projectId,
      customCategories: getCustomCategories(),
      teamId: teamId || "",
      ...getEurekaDetails(false),
    };

    params.imagesLibrary = params.imagesLibrary.map((image) => {
      const imageCopy = { ...image };
      delete imageCopy.imageSource;
      return imageCopy;
    });

    delete params.image.imageSource;

    setIsPending({ ...isPending, update: true });
    const onSuccess = () => {
      handleClose(shouldClose, { ...isPending, update: false });
      setIsLoadingImage(false);
    };

    const onError = (error) => {
      console.error(error);
      handleClose(shouldClose, { ...isPending, update: false });
    };

    const issues = getIssuesWithMessages() || [];

    const issuesParams = {
      ideaId: eurekaId || "",
      issues,
    };

    const onSuccessUpdateIssues = () => {};

    const onErrorUpdateIssues = (error) => {
      console.error(error);
    };

    if (shouldClose && !isPending.update) {
      createIssuesWithMessages(issuesParams, onSuccessUpdateIssues, onErrorUpdateIssues);
    }

    updateIdea(params, onSuccess, onError);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    handleSetEureka({ ...getEureka(), [name]: value });
  };

  const handleAddIssue = () => {
    const currentDependency = getEureka();

    const lastIssue = currentDependency?.issues[currentDependency?.issues?.length - 1];

    if (lastIssue?.comment || currentDependency?.issues?.length === 0) {
      handleSetEureka({
        ...currentDependency,
        issues: [...currentDependency.issues, { comment: "", description: "", createdByUser: "" }],
      });
    }
  };

  const handleRemoveIssue = (index) => {
    const currentDependency = getEureka();
    const filteredIssues = currentDependency.issues.filter((issue, issueIndex) => issueIndex !== index);
    const filteredMessages = currentDependency.issuesMessages.map((message) => {
      if (message.issueIndex > index) {
        return { ...message, issueIndex: --message.issueIndex };
      } else if (message.issueIndex === index) {
        return {};
      }
      return message;
    });
    handleSetEureka({
      ...currentDependency,
      issues: [...filteredIssues],
      issuesMessages: [...filteredMessages],
      deletedIssues: [
        ...(currentDependency.deletedIssues ? currentDependency.deletedIssues : []),
        { id: currentDependency.issues[index].id },
      ],
    });
  };

  const removeLastIssue = (index, idIssues) => {
    const currentEureka = getEureka();
    const foundIssue = currentEureka.issues.find((_, issueIndex) => issueIndex === index);

    let filteredMessages = [];

    if (idIssues) {
      filteredMessages = currentEureka.issuesMessages.filter((message) => idIssues !== message.issueId);
    } else {
      filteredMessages = currentEureka.issuesMessages.filter((message) => message.issueIndex !== index);
    }

    foundIssue.comment = "";
    foundIssue.description = "";
    foundIssue.createdByUser = "";

    handleSetEureka({
      ...currentEureka,
      issues: [...currentEureka.issues],
      issuesMessages: [...filteredMessages],
      deletedIssues: [
        ...(currentEureka.deletedIssues ? currentEureka.deletedIssues : []),
        { id: currentEureka.issues[index].id },
      ],
    });
  };

  const removeMessage = (indexMessage, idSavedMessage) => {
    const currentEureka = getEureka();
    let filteredMessages = [];

    if (idSavedMessage) {
      filteredMessages = currentEureka.issuesMessages.filter((message) => idSavedMessage !== message._id);
    } else {
      filteredMessages = currentEureka.issuesMessages.filter((message, index) => index !== indexMessage);
    }

    handleSetEureka({
      ...currentEureka,
      issuesMessages: [...filteredMessages],
    });
  };

  const removeLastMessage = (indexMessage, issueId, idSavedMessage) => {
    const currentEureka = getEureka();
    let foundMessage = {};

    if (idSavedMessage) {
      foundMessage = currentEureka.issuesMessages.find((message) => idSavedMessage === message._id);
    } else {
      foundMessage = currentEureka.issuesMessages.find(
        (message, index) => indexMessage === index && issueId === message.issueIndex,
      );
    }

    foundMessage.message = "";

    handleSetEureka({
      ...currentEureka,
      issuesMessages: [...currentEureka.issuesMessages],
    });
  };

  const handleIssueChange = (index, value, name) => {
    const currentDependency = { ...getEureka() };
    const changedIssues = [...currentDependency.issues];
    if (name === "comment") {
      changedIssues[index].comment = value;
      changedIssues[index].createdByUser = `${props.userFullName.name} ${props.userFullName.lastName}`;
    }

    if (name === "description") {
      changedIssues[index].description = value;
    }

    handleSetEureka({ ...currentDependency, issues: [...changedIssues] });
  };

  const handleAddIssueMessage = (issueId, issueIndex) => {
    const currentDependency = getEureka();
    const filteredMessages =
      currentDependency?.issuesMessages.filter((message) =>
        message.issueId ? message.issueId === issueId : message.issueIndex === issueIndex,
      ) || [];

    const lastMessage = filteredMessages[filteredMessages.length - 1];
    const canBeCreated = !lastMessage || lastMessage.message !== "";

    if (canBeCreated) {
      handleSetEureka({
        ...currentDependency,
        issuesMessages: [...currentDependency.issuesMessages, { message: "", issueId, issueIndex }],
      });
    }
  };

  const handleIssueMessageChange = (value, index, userId, issueIndex) => {
    const currentEureka = getEureka();
    const changedIssuesMessages = [...currentEureka.issuesMessages];
    changedIssuesMessages[index] = { message: value, userId, issueIndex };
    handleSetEureka({ ...currentEureka, issuesMessages: changedIssuesMessages });
  };

  const updateBenefit = (name, index, eureka, value) => {
    const changedBenefits = [...eureka.potentialBenefitsOfEureka];
    const newBenefit = { ...changedBenefits[index] };
    newBenefit[name] = value;
    changedBenefits[index] = newBenefit;
    handleSetEureka({ ...eureka, potentialBenefitsOfEureka: changedBenefits });
  };

  const handleTargetChange = (index, { id, name }) => {
    const currentEureka = getEureka();

    const updatedTarget = [...currentEureka.potentialBenefitsOfEureka[index].target];
    const indexUpdatedTarget = updatedTarget.findIndex((target) => target.name === name);

    if (indexUpdatedTarget === -1) {
      updatedTarget.push({ id, name });
    } else {
      updatedTarget.splice(indexUpdatedTarget, 1);
    }

    updateBenefit("target", index, currentEureka, updatedTarget);
  };

  const handleBenefitChange = (index, value, name) => {
    const currentEureka = getEureka();
    updateBenefit(name, index, currentEureka, value);
  };

  const handleDeleteOption = (changedLabel) => {
    const currentEureka = getEureka();

    currentEureka.potentialBenefitsOfEureka.forEach((benefit) => {
      const updatedTargets = benefit.target.filter((targetElement) => targetElement.name !== changedLabel);
      benefit.target = updatedTargets;
    });

    handleSetEureka({
      ...currentEureka,
      potentialBenefitsOfEureka: currentEureka.potentialBenefitsOfEureka,
    });

    removeCustomCategory(changedLabel);
  };

  const addBenefit = () => {
    const currentEureka = getEureka();
    handleSetEureka({
      ...currentEureka,
      potentialBenefitsOfEureka: [
        ...currentEureka.potentialBenefitsOfEureka,
        { name: "", description: "", target: [], userInfo: "" },
      ],
    });
  };

  const removeBenefit = (index) => {
    const currentEureka = getEureka();
    removeCustomCategory(currentEureka.potentialBenefitsOfEureka[index].target);
    const filteredBenefits = currentEureka.potentialBenefitsOfEureka.filter(
      (benefit, benefitIndex) => benefitIndex !== index,
    );
    handleSetEureka({ ...currentEureka, potentialBenefitsOfEureka: [...filteredBenefits] });
  };

  const removeLastBenefit = (index) => {
    const currentEureka = getEureka();
    removeCustomCategory(currentEureka.potentialBenefitsOfEureka[index].target);
    const foundBenefit = currentEureka.potentialBenefitsOfEureka.find(
      (benefit, benefitIndex) => benefitIndex === index,
    );

    foundBenefit.name = "";
    foundBenefit.description = "";
    foundBenefit.target = [];
    foundBenefit.userInfo = "";

    handleSetEureka({
      ...currentEureka,
      potentialBenefitsOfEureka: [...currentEureka.potentialBenefitsOfEureka],
    });
  };

  const handleSetCurrentTab = (value) => {
    setCurrentTab(value);
    handleSave();
  };

  const handleSave = () => {
    const upToDate = compareIdeas(savedEureka, eureka);

    if (!upToDate) {
      const updatedSubtraction = { ...getEureka() };

      setSavedEureka(updatedSubtraction);
      isNew ? createEureka() : updateEureka();
    }
  };

  const compareIdeas = (firstItem, secondItem) => {
    const keys = Object.keys(firstItem);
    let isEqual = true;

    for (let key of keys) {
      if (key === "potentialBenefitsOfEureka") {
        const firstBenefit =
          firstItem[key] &&
          firstItem[key].filter((benefit) => benefit.name && benefit.target && benefit.description && benefit.userInfo);
        const secondBenefit =
          secondItem[key] &&
          secondItem[key].filter(
            (benefit) => benefit.name && benefit.target && benefit.description && benefit.userInfo,
          );

        if (firstBenefit.length !== secondBenefit.length) {
          isEqual = false;
          break;
        }

        for (let benefit of firstBenefit) {
          const foundBenefit = secondBenefit.find(
            (item) =>
              item.name === benefit.name &&
              item.target === benefit.target &&
              item.description === benefit.description &&
              item.userInfo === benefit.userInfo,
          );
          if (!foundBenefit) {
            isEqual = false;
            break;
          }
        }

        continue;
      }

      if (key === "issues") {
        if (firstItem[key].join("") !== secondItem[key].join("")) {
          isEqual = false;
          break;
        }
        continue;
      }

      if (key === "issuesMessages") {
        if (firstItem[key].join("") !== secondItem[key].join("")) {
          isEqual = false;
          break;
        }
        continue;
      }

      if (key === "image") {
        if (firstItem[key].name !== secondItem[key].name) {
          isEqual = false;
          break;
        }
        continue;
      }

      if (firstItem[key] !== secondItem[key]) {
        isEqual = false;
        break;
      }
    }

    return isEqual;
  };

  const prevEnabled = () => {
    if (currentTab === 0) {
      return false;
    }

    return true;
  };

  const nextEnabled = () => {
    if (currentTab === 2) {
      return false;
    }

    return true;
  };

  const isCompleted = () => {
    const currentEureka = getEureka();
    return currentEureka.isIdeaFeasible === false || (currentEureka.isIdeaFeasible === true && currentEureka.score > 0);
  };

  const handleSetEureka = (value) => {
    setEureka(value);
  };

  const handleImageUpload = (file, imageSource = null) => {
    const CancelToken = axios.CancelToken;

    const formData = new FormData();
    formData.append("image", file, file.name);
    updateUploadInfo({ progress: 0, cancel: null, uploaded: false, name: file.name, size: file.size });

    const config = {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        const completed = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        updateUploadInfo({ progress: completed });
      },
      cancelToken: new CancelToken(function executor(c) {
        updateUploadInfo({ cancel: c });
      }),
    };

    const onSuccess = (response) => {
      updateUploadInfo({ uploaded: true, date: new Date(), uploadName: response.filename, id: response.imageId });
      const image = {
        name: response.filename,
        originalName: file.name,
        id: response.imageId,
        imageSource,
      };

      const currentEureka = getEureka();
      if (!currentEureka.imagesLibrary.length) {
        handleSetEureka({ ...currentEureka, imagesLibrary: [...currentEureka.imagesLibrary, image], image });
        return;
      }
      handleSetEureka({ ...currentEureka, imagesLibrary: [...currentEureka.imagesLibrary, image] });
    };

    const onError = (error) => {
      console.error(error);
      if (uploadInfo.name) {
        updateUploadInfo({ ...initialUpload });
      }
    };

    setIsLoadingImage(true);
    uploadImage(formData, config, onSuccess, onError);
  };

  const handleCancelClick = () => {
    setNewGeneratedImage({
      isNew: false,
      urlImage: "",
    });
    uploadInfo.cancel && uploadInfo.cancel("Canceled by user");
    updateUploadInfo({ ...initialUpload });

    const currentSubtraction = getEureka();
    handleSetEureka({ ...currentSubtraction, image: initialState.image });
  };

  const updateUploadInfo = (value) => {
    if (value) {
      setUploadInfo((state) => ({ ...state, ...value }));
    } else {
      setUploadInfo((state) => ({ ...state, ...initialUpload }));
    }
  };

  const currentUploadInfo = uploadInfo;

  const handleCloseDialog = () => {
    setIsClosing(true);
    const upload = uploadInfo;
    if (upload.name && !upload.uploaded) {
      upload.cancel && upload.cancel("Canceled by user");
    }

    setUploadInfo({ ...initialUpload });
    closeDialog && closeDialog();
  };

  const matrixBodyClass = [1, 2].includes(currentTab) ? " benefits-container" : "";

  const addCustomCategory = (name) => {
    const foundCategory = customCategories.find((category) => category.name === name);
    if (foundCategory) {
      foundCategory.count++;
      return;
    }

    const currentId = customCategories[customCategories.length - 1]?.id;

    setCustomCategories([
      ...customCategories,
      {
        id: _.isNumber(currentId) ? currentId + 1 : 3,
        name,
        count: 1,
      },
    ]);
  };

  const removeCustomCategory = (name) => {
    const updatedCategories = customCategories.filter((category) => category.name !== name);
    setCustomCategories(updatedCategories);
  };

  const renameCustomCategory = (oldName, newName) => {
    const foundCategoryIndex = customCategories.findIndex((category) => category.name === newName);
    if (foundCategoryIndex !== -1) {
      customCategories[foundCategoryIndex].count++;

      return;
    }

    const eureka = getEureka();

    eureka.potentialBenefitsOfEureka.forEach((benefit) => {
      const currentTargetIndex = benefit.target.findIndex((target) => target.name === oldName);

      if (currentTargetIndex !== -1) {
        benefit.target.splice(currentTargetIndex, 1, { id: benefit.target[currentTargetIndex].id, name: newName });
      }
    });

    handleSetEureka({
      ...eureka,
      potentialBenefitsOfEureka: eureka.potentialBenefitsOfEureka,
    });

    const copiedArray = [...customCategories];
    const findOldCategoryIndex = customCategories.findIndex((category) => category.name === oldName);

    const { count, id } = customCategories[findOldCategoryIndex];

    copiedArray.splice(findOldCategoryIndex, 1, { id, count, name: newName });
    setCustomCategories(copiedArray);
  };

  const getCustomCategories = () => {
    return customCategories.filter((category) => category.count >= 0);
  };

  const addIdeasName = (ideasName) => {
    setEureka({ ...eureka, name: ideasName });
    setOpenGenerateModal(false);
    setSuggestedOptions([]);
  };

  const checkDescription = () => {
    const currentEureka = getEureka();

    if (!currentEureka.description) {
      setError({
        openErrorModal: true,
        message: getTranslation("MESSAGE_MODAL_CHECK_DESCRIPTION_TEXT"),
        headerError: getTranslation("MESSAGE_MODAL_CHECK_DESCRIPTION_HEADER"),
      });
      return false;
    }

    return true;
  };

  const generateNames = () => {
    if (!checkDescription()) return;

    setOpenGenerateModal(true);

    const onSuccess = ({ parsedChoices, numberRequests }) => {
      setSuggestedOptions(parsedChoices);
      setSuggestedLoading(false);
      closeErrorWindow();
    };

    const onError = (error) => {
      setIsOpenGenerateImageModal(false);
      const { message, code } = error.response && error.response.data;

      if (code === "30840" || code === "30841") {
        setOpenGenerateModal(false);
        setIsOpenWarningModal(true);
        setSuggestedLoading(false);
        setIsEnoghTokensForImage(code !== "30841");
        return;
      }

      setError({
        openErrorModal: true,
        message: getTranslation(message) || error,
        headerError: getTranslation("ERROR_MESSAGE_MODAL_HEADER"),
      });
      setSuggestedLoading(false);
    };

    setSuggestedLoading(true);

    const currentEureka = getEureka();

    getGeneratedNamesForIdea(
      {
        projectName: productName ? productName : projectName,
        productType,
        projectId,
        descriptionOfIdea: currentEureka.description,
        language,
      },
      onSuccess,
      onError,
    );
  };

  const selectGenerateOption = (nameOption) => {
    setSelectedGenerateOptions((prev) => {
      const prevSelectedOption = new Set(prev);
      const key = `${nameOption}`;
      prev.has(key) ? prevSelectedOption.delete(key) : prevSelectedOption.add(key);
      return prevSelectedOption;
    });
  };

  const addGenerateBenefits = () => {
    const benefits = [];

    suggestedOptions.map((benefit) => {
      if (selectedGenerateOptions.has(`${benefit}`)) {
        const params = {
          name: benefit.includes("：") ? benefit.split("：")[0] : benefit.split(":")[0],
          description: benefit.includes("：") ? benefit.split("：")[1] : benefit.split(":")[1],
          target: [],
          userInfo: "AI",
        };
        benefits.push(params);
      }
    });

    setEureka({
      ...eureka,
      potentialBenefitsOfEureka: [...benefits, ...eureka.potentialBenefitsOfEureka],
    });

    setOpenGenerateModal(false);
    setSelectedGenerateOptions(new Set());
    setSuggestedOptions([]);
  };

  const generateBenefits = () => {
    if (!checkDescription()) return;

    setOpenGenerateModal(true);

    const onSuccess = ({ parsedChoices, numberRequests }) => {
      setSuggestedOptions(parsedChoices);
      setSuggestedLoading(false);
      closeErrorWindow();
    };

    const onError = (error) => {
      setIsOpenGenerateImageModal(false);
      const { message, code } = error.response && error.response.data;

      if (code === "30840" || code === "30841") {
        setOpenGenerateModal(false);
        setIsOpenWarningModal(true);
        setSuggestedLoading(false);
        setIsEnoghTokensForImage(code !== "30841");
        return;
      }

      setError({
        openErrorModal: true,
        message: getTranslation(message) || error,
        headerError: getTranslation("ERROR_MESSAGE_MODAL_HEADER"),
      });
      setSuggestedLoading(false);
    };

    setSuggestedLoading(true);

    const currentEureka = getEureka();

    getGeneratedBenefits(
      {
        projectName: productName ? productName : projectName,
        projectId: projectId,
        descriptionOfIdea: currentEureka.description,
        language,
      },
      onSuccess,
      onError,
    );
  };

  const closeGenerateModal = () => {
    setOpenGenerateModal(false);
    setSuggestedOptions([]);
  };

  const closeErrorWindow = () => {
    setError({
      openErrorModal: false,
      message: "",
      headerError: "",
    });
  };

  const handleChangeScore = (e) => {
    setEureka({ ...eureka, score: e });
  };

  const handleChangeFeasibleScore = (e) => {
    setEureka({ ...eureka, isIdeaFeasible: e > 0, feasibleScore: e });
  };

  const getMessageForCheckRating = () => {
    return (
      <p className="flex-column mt-25px">
        <span style={{ margin: "0" }}>{getTranslation("RATING_REQUIRED_MODAL_NOT_RATE_IMPACT")}</span>
        <span>{getTranslation("RATING_REQUIRED_MODAL_NOT_RATE_FEASIBILITY")}</span>
      </p>
    );
  };

  const checkRating = () => {
    if (
      currentTab === 2 &&
      (eureka.score === null || eureka.score === undefined) &&
      (eureka.feasibleScore === null || eureka.feasibleScore === undefined)
    ) {
      setError({
        openErrorModal: true,
        message: getMessageForCheckRating(),
        headerError: getTranslation("RATING_REQUIRED_MODAL_HEADER"),
      });
      return;
    }

    if (currentTab === 2 && eureka.score && (eureka.feasibleScore === null || eureka.feasibleScore === undefined)) {
      setError({
        openErrorModal: true,
        message: getTranslation("RATING_REQUIRED_MODAL_NOT_RATE_FEASIBILITY"),
        headerError: getTranslation("RATING_REQUIRED_MODAL_HEADER"),
      });
      return;
    }

    if (currentTab === 2 && eureka.feasibleScore && (eureka.score === null || eureka.score === undefined)) {
      setError({
        openErrorModal: true,
        message: getTranslation("RATING_REQUIRED_MODAL_NOT_RATE_IMPACT"),
        headerError: getTranslation("RATING_REQUIRED_MODAL_HEADER"),
      });
      return;
    }

    closeErrorWindow();

    isNew ? createEureka(true) : updateEureka(true);
  };

  const handleRightClick = () => {
    if (currentTab === 1 && (eureka.score === null || eureka.score === undefined)) {
      setError({
        openErrorModal: true,
        message: getTranslation("RATING_REQUIRED_MODAL_NOT_RATE_IMPACT"),
        headerError: getTranslation("RATING_REQUIRED_MODAL_HEADER"),
      });
      return;
    }

    closeErrorWindow();
    handleSetCurrentTab(currentTab + 1);
  };

  const handleLeftClick = () => {
    handleSetCurrentTab(currentTab - 1);
  };

  const changeCurrentTab = (index) => {
    setCurrentTab(index);
  };

  const generateChallenges = () => {
    if (!checkDescription()) return;

    setOpenGenerateModal(true);

    const onSuccess = ({ parsedChoices, numberRequests }) => {
      setSuggestedOptions(parsedChoices);
      setSuggestedLoading(false);
      closeErrorWindow();
    };

    const onError = (error) => {
      setIsOpenGenerateImageModal(false);
      const { message, code } = error.response && error.response.data;

      if (code === "30840" || code === "30841") {
        setOpenGenerateModal(false);
        setIsOpenWarningModal(true);
        setSuggestedLoading(false);
        setIsEnoghTokensForImage(code !== "30841");
        return;
      }

      setError({
        openErrorModal: true,
        message: getTranslation(message) || error,
        headerError: getTranslation("ERROR_MESSAGE_MODAL_HEADER"),
      });
      setSuggestedLoading(false);
    };

    setSuggestedLoading(true);

    const currentEureka = getEureka();

    getGeneratedChallenges(
      {
        projectName: productName ? productName : projectName,
        projectId: projectId,
        descriptionOfIdea: currentEureka.description,
        language,
      },
      onSuccess,
      onError,
    );
  };

  const addGenerateChallenges = () => {
    const generatedIssues = [];

    suggestedOptions.map((issue) => {
      if (selectedGenerateOptions.has(`${issue}`)) {
        const params = {
          comment: issue.includes("：") ? issue.split("：")[0] : issue.split(":")[0],
          description: issue.includes("：") ? issue.split("：")[1] : issue.split(":")[1],
          createdByUser: "AI",
        };
        generatedIssues.push(params);
      }
    });

    setEureka({
      ...eureka,
      issues: [...generatedIssues, ...eureka.issues],
    });

    setOpenGenerateModal(false);
    setSelectedGenerateOptions(new Set());
    setSuggestedOptions([]);
  };

  const convertImage = async (imageBase64) => {
    try {
      const base64Response = await fetch(`data:image/png;base64,${imageBase64}`);
      const blobImage = await base64Response.blob();

      let dt = new DataTransfer();
      dt.items.add(new File([blobImage], "image.png", { type: blobImage.type }));
      const fileImage = dt.files[0];
      const bufferImage = await fileImage.arrayBuffer();

      const reader = new FileReader();

      reader.onload = () => {
        const imageUrl = `data:image/png;base64,${Buffer.from(bufferImage).toString("base64")}`;
        setPotentialGeneratedImageUrl(imageUrl);
      };

      reader.readAsArrayBuffer(fileImage);

      return fileImage;
    } catch (error) {
      setError({
        openErrorModal: true,
        message: error,
        headerError: getTranslation("ERROR_MESSAGE_MODAL_HEADER"),
      });
    }
  };

  const handleAcceptPotentialImage = () => {
    setGeneratedImage(potentialGeneratedImageUrl);
    handleImageUpload(potentialGeneratedImageFile, potentialGeneratedImageUrl);
    setIsOpenGenerateImageModal(false);
  };

  const generateImage = () => {
    if (!checkDescription()) return;

    setIsOpenGenerateImageModal(true);

    const onSuccess = async (imageBase64) => {
      setNewGeneratedImage({
        isNew: true,
        urlImage: `data:image/png;base64,${imageBase64}`,
      });
      const fileImage = await convertImage(imageBase64);

      setPotentialGeneratedImageFile(fileImage);
      setSuggestedLoading(false);
    };

    const onError = (error) => {
      setIsOpenGenerateImageModal(false);
      const { message, code } = error.response && error.response.data;

      if (code === "30840" || code === "30841") {
        setOpenGenerateModal(false);
        setIsOpenWarningModal(true);
        setSuggestedLoading(false);
        setIsEnoghTokensForImage(code !== "30841");
        return;
      }

      setError({
        openErrorModal: true,
        message: getTranslation(message) || error,
        headerError: getTranslation("ERROR_MESSAGE_MODAL_HEADER"),
      });
      setSuggestedLoading(false);
    };

    setSuggestedLoading(true);

    const currentEureka = getEureka();

    getGeneratedImage(
      {
        descriptionOfIdea: currentEureka.description,
        language,
      },
      onSuccess,
      onError,
    );
  };

  return (
    <Dialog closeDialog={handleCloseDialog} closeOnClickOutside={false}>
      <div className="subtraction-dialog-wrapper">
        <EurekaModalHeader
          closeDialog={handleCloseDialog}
          subtraction={getEureka()}
          currentTab={currentTab}
          setCurrentTab={handleSetCurrentTab}
          isCompletedIdea={isCompletedIdea}
          projectName={projectName}
          changeCurrentTab={changeCurrentTab}
          productName={productName}
        />

        <div className={"subtraction-dialog-body-eureka flex-column" + matrixBodyClass}>
          <EurekaModalTabs
            selectCurrentEurekaImageFromLibrary={selectCurrentEurekaImageFromLibrary}
            removeImageFromLibrary={removeImageFromLibrary}
            handleAcceptPotentialImage={handleAcceptPotentialImage}
            potentialGeneratedImageUrl={potentialGeneratedImageUrl}
            currentTab={currentTab}
            eureka={getEureka()}
            setEureka={handleSetEureka}
            handleChange={handleChange}
            userFullName={props.userFullName}
            userId={props.userId}
            addBenefit={addBenefit}
            removeBenefit={removeBenefit}
            removeLastBenefit={removeLastBenefit}
            handleBenefitChange={handleBenefitChange}
            handleTargetChange={handleTargetChange}
            addIssue={handleAddIssue}
            removeIssue={handleRemoveIssue}
            removeLastIssue={removeLastIssue}
            removeMessage={removeMessage}
            removeLastMessage={removeLastMessage}
            handleIssueChange={handleIssueChange}
            uploadInfo={currentUploadInfo}
            newGeneratedImage={newGeneratedImage}
            handleCancelClick={handleCancelClick}
            handleFileSelect={handleImageUpload}
            customCategories={getCustomCategories()}
            isLoadingImage={isLoadingImage}
            addIssueMessage={handleAddIssueMessage}
            handleIssueMessageChange={handleIssueMessageChange}
            handleGenerateNames={generateNames}
            suggestedLoading={suggestedLoading}
            addIdeasName={addIdeasName}
            handleChangeScore={handleChangeScore}
            handleChangeFeasibleScore={handleChangeFeasibleScore}
            suggestedOptions={suggestedOptions}
            openGenerateModal={openGenerateModal}
            closeGenerateModal={closeGenerateModal}
            selectGenerateOption={selectGenerateOption}
            selectedGenerateOptions={selectedGenerateOptions}
            addGenerateBenefits={addGenerateBenefits}
            handleGenerateBenefits={generateBenefits}
            handleGenerateChallenges={generateChallenges}
            addGenerateChallenges={addGenerateChallenges}
            handleGenerateImage={generateImage}
            isOpenGenerateImageModal={isOpenGenerateImageModal}
            closeDialogGenerateImage={() => setIsOpenGenerateImageModal(false)}
            generatedImage={generatedImage}
            handleDeleteOption={handleDeleteOption}
            handleRenameOption={renameCustomCategory}
            addCustomCategory={addCustomCategory}
          />

          <IdeaNavigationFooter
            resetIdea={resetEureka}
            prevEnabled={prevEnabled}
            nextEnabled={nextEnabled}
            handleRightClick={handleRightClick}
            handleLeftClick={handleLeftClick}
            disabledNextButton={disabledNextButton}
            undoResetIdea={undoResetIdea}
            checkedTab={checkedTab}
          />
        </div>

        <IdeaModalFooter
          deleteIdea={handleOpenConfirmationModalOpen}
          isCompleted={isCompleted}
          checkRating={checkRating}
          isDisabledSave={isDisabledSave}
          currentTab={currentTab}
        />

        {isConfirmationModalOpen && (
          <ConfirmationModal
            className="delete-conformation-modal"
            textContainerClass="span-delete-modal"
            closeDialog={handleCloseConfirmationModalOpen}
            message={getTranslation("CONFIRM_MODAL_DELETE_IDEA")}
            autoFocus={true}
            buttonText={getTranslation("EDIT_ATTRIBUTE_DIALOG_BUTTON_DELETE")}
            onConfirm={archivedEureka}
          />
        )}

        {isConfirmationDeleteModalOpen && (
          <TrashModal
            closeDialog={handleCloseConfirmationDeleteModalOpen}
            onProceed={deleteEureka}
            history={history}
            itemType="ideas"
            itemsCount={0}
          />
        )}

        {error.openErrorModal && (
          <ErrorMessageModal
            message={error.message}
            closeModalWindow={closeErrorWindow}
            textButton={getTranslation("CONFIRMATION_MODAL_DEFAULT_BUTTON_TEXT")}
            handleButton={closeErrorWindow}
            header={error.headerError}
          />
        )}

        {paymentInfo.key && (
          <CheckoutModal
            isOpenAISubscription
            closeDialog={() => setPaymentInfo((prev) => ({ ...prev, key: "" }))}
            subscriptionInfo={paymentInfo}
            history={history}
            mode="payment"
            setGeneratedOpenAIRequests={setGeneratedOpenAIRequests}
          />
        )}

        {isOpenBuyCreditsgModal && (
          <BuyMoreAICreditsModal setPaymentInfo={setPaymentInfo} closeDialog={() => setIsOpenBuyCreditsgModal(false)} />
        )}

        {isOpenWarningModal && (
          <WarningNotTokensModal
            closeDialog={() => {
              setIsOpenWarningModal(false);
            }}
            openBuyModal={() => setIsOpenBuyCreditsgModal(true)}
            history={history}
            isEnoghTokensForImage={isEnoghTokensForImage}
          />
        )}
      </div>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  language: state.auth.userInfo.language,
});

const mapDispatchToProps = {
  setGeneratedOpenAIRequests,
};

export default connect(mapStateToProps, mapDispatchToProps)(EurekaEditModal);
